import React, { useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Divider,
} from '@mui/material'
import {
  ShoppingCart,
  People,
  AttachMoney,
  Assessment,
  TrendingUp,
  Add,
  CheckCircle,
  Schedule,
  Refresh,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { useDashboardUpdates } from '../../hooks/useWebSocket'
import { dashboardAPI } from '../../services/api'
import { updateDashboardMetrics } from '../../store/slices/realtimeSlice'
import { MobileCardGrid, MobileMetricCard, MobileFAB } from '../../components/Mobile'
import type { MobileCardGridItem } from '../../components/Mobile'
import { usePullToRefresh } from '../../hooks/useSwipeGesture'
import { createStaggerAnimation } from '../../utils/mobileAnimations'
import PWAInstallButton from '../../components/PWA/PWAInstallButton'

/**
 * Dashboard Mobile View
 * Optimized mobile-first dashboard with card-based layout
 */
export const DashboardMobileView: React.FC = () => {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const { dashboardMetrics, recentActivities } = useAppSelector((state) => state.realtime)
  const { metrics } = useDashboardUpdates()

  // Pull to refresh
  const { handlers, isPulling, pullDistance, isRefreshing } = usePullToRefresh(async () => {
    const metricsData = await dashboardAPI.getMetrics()
    dispatch(updateDashboardMetrics(metricsData))
  })

  // Load initial data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const metricsData = await dashboardAPI.getMetrics()
        dispatch(updateDashboardMetrics(metricsData))
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      }
    }
    loadDashboardData()
  }, [dispatch])

  // Update metrics when WebSocket data changes
  useEffect(() => {
    if (metrics && Object.keys(metrics).length > 0) {
      dispatch(updateDashboardMetrics(metrics))
    }
  }, [metrics, dispatch])

  // Quick action cards
  const quickActions: MobileCardGridItem[] = [
    {
      id: 'create-order',
      title: 'New Order',
      icon: <Add />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      onClick: () => {
        // Navigate to create order
        window.location.href = '/orders'
      },
    },
    {
      id: 'mark-attendance',
      title: 'Attendance',
      icon: <CheckCircle />,
      gradient: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
      onClick: () => {
        // Navigate to attendance
        window.location.href = '/employees'
      },
    },
    {
      id: 'view-orders',
      title: 'Orders',
      icon: <ShoppingCart />,
      value: dashboardMetrics.activeOrders || 0,
      gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
      onClick: () => {
        window.location.href = '/orders'
      },
    },
    {
      id: 'view-reports',
      title: 'Reports',
      icon: <Assessment />,
      gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      onClick: () => {
        // Navigate to reports
      },
    },
  ]

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <ShoppingCart sx={{ color: 'primary.main' }} />
      case 'employee':
        return <People sx={{ color: 'success.main' }} />
      case 'attendance':
        return <Schedule sx={{ color: 'info.main' }} />
      default:
        return <Assessment sx={{ color: 'text.secondary' }} />
    }
  }

  return (
    <Box
      sx={{
        pb: 10, // Space for bottom navigation
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
      onTouchStart={handlers.onTouchStart as any}
      onTouchMove={handlers.onTouchMove as any}
      onTouchEnd={handlers.onTouchEnd as any}
    >
      {/* Pull to Refresh Indicator */}
      {isPulling && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: pullDistance,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            zIndex: 1000,
            transition: 'height 0.2s',
          }}
        >
          {isRefreshing ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            <Typography variant="caption">Pull to refresh</Typography>
          )}
        </Box>
      )}

      {/* Welcome Card */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 0,
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(103, 126, 234, 0.3)',
          ...createStaggerAnimation(0, 50),
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -100,
            right: -100,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(60px)',
          },
        }}
      >
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                fontSize: '1.75rem',
                fontWeight: 700,
                border: '3px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              {user?.full_name?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                Welcome back!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95, fontWeight: 500 }}>
                {user?.full_name}
              </Typography>
            </Box>
            <IconButton
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  transform: 'rotate(180deg)',
                },
                transition: 'all 0.3s ease',
              }}
              onClick={async () => {
                const metricsData = await dashboardAPI.getMetrics()
                dispatch(updateDashboardMetrics(metricsData))
              }}
            >
              <Refresh />
            </IconButton>
          </Box>

          {/* Circular Progress */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={140}
                thickness={3}
                sx={{
                  color: 'rgba(255, 255, 255, 0.2)',
                }}
              />
              <CircularProgress
                variant="determinate"
                value={dashboardMetrics.productionEfficiency || 0}
                size={140}
                thickness={3}
                sx={{
                  color: 'white',
                  position: 'absolute',
                  left: 0,
                  filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 800, textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>
                  {dashboardMetrics.productionEfficiency || 0}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 600, mt: 0.5 }}>
                  Efficiency
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ px: 2.5 }}>
        {/* PWA Install Banner */}
        <Box sx={{ mb: 3, ...createStaggerAnimation(1, 50) }}>
          <PWAInstallButton variant="banner" />
        </Box>

        {/* Metric Cards */}
        <Box sx={{ mb: 4, ...createStaggerAnimation(2, 50) }}>
          <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 700, color: 'text.primary' }}>
            Overview
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MobileMetricCard
              title="Total Orders"
              value={dashboardMetrics.totalOrders || 0}
              icon={<ShoppingCart />}
              subtitle="All time"
              trend={5.2}
              trendLabel="vs last month"
              color="primary"
            />
            <MobileMetricCard
              title="Active Orders"
              value={dashboardMetrics.activeOrders || 0}
              icon={<Assessment />}
              subtitle="In progress"
              color="warning"
            />
            <MobileMetricCard
              title="Total Employees"
              value={dashboardMetrics.totalEmployees || 0}
              icon={<People />}
              subtitle="Active staff"
              trend={2.1}
              color="success"
            />
            <MobileMetricCard
              title="Monthly Revenue"
              value={`Rs. ${(dashboardMetrics.monthlyRevenue || 0).toLocaleString()}`}
              icon={<AttachMoney />}
              subtitle="This month"
              trend={8.7}
              trendLabel="vs last month"
              color="info"
            />
          </Box>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 4, ...createStaggerAnimation(3, 50) }}>
          <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 700, color: 'text.primary' }}>
            Quick Actions
          </Typography>
          <MobileCardGrid items={quickActions} columns={2} spacing={2.5} />
        </Box>

        {/* Performance Metrics */}
        <Box sx={{ mb: 4, ...createStaggerAnimation(4, 50) }}>
          <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 700, color: 'text.primary' }}>
            Performance
          </Typography>
          <Card sx={{
            borderRadius: 4,
            mb: 2.5,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(56, 142, 60, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'rgba(76, 175, 80, 0.2)',
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.15)',
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Order Completion
                  </Typography>
                </Box>
                <Typography variant="h5" color="success.main" sx={{ fontWeight: 700 }}>
                  {dashboardMetrics.orderCompletionRate || 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={dashboardMetrics.orderCompletionRate || 0}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #4caf50 0%, #388e3c 100%)',
                    borderRadius: 5,
                  },
                }}
              />
            </CardContent>
          </Card>

          <Card sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(25, 118, 210, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'rgba(33, 150, 243, 0.2)',
            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(33, 150, 243, 0.15)',
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People sx={{ color: 'info.main', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Employee Utilization
                  </Typography>
                </Box>
                <Typography variant="h5" color="info.main" sx={{ fontWeight: 700 }}>
                  {dashboardMetrics.employeeUtilization || 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={dashboardMetrics.employeeUtilization || 0}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: 'rgba(33, 150, 243, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #2196f3 0%, #1976d2 100%)',
                    borderRadius: 5,
                  },
                }}
              />
            </CardContent>
          </Card>
        </Box>

        {/* Recent Activity */}
        <Box sx={{ mb: 4, ...createStaggerAnimation(5, 50) }}>
          <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 700, color: 'text.primary' }}>
            Recent Activity
          </Typography>
          <Card sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.08)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          }}>
            <List sx={{ p: 0 }}>
              {recentActivities.slice(0, 5).map((activity, index) => (
                <React.Fragment key={activity.id}>
                  {index > 0 && <Divider sx={{ my: 0.5 }} />}
                  <ListItem sx={{
                    py: 2.5,
                    px: 2,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(103, 126, 234, 0.05)',
                    },
                  }}>
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(103, 126, 234, 0.1)',
                      }}>
                        {getActivityIcon(activity.type)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.title}
                      secondary={activity.description}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                        sx: { mt: 0.5 },
                      }}
                    />
                    <Chip
                      label={new Date(activity.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: 'rgba(103, 126, 234, 0.1)',
                        color: 'primary.main',
                      }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
              {recentActivities.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No recent activity"
                    secondary="Activity will appear here"
                    sx={{ textAlign: 'center' }}
                  />
                </ListItem>
              )}
            </List>
          </Card>
        </Box>
      </Box>

    </Box>
  )
}
